import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  HStack,
  VStack,
  Text,
  Button,
  IconButton,
  Badge,
  Avatar,
  Drawer,
  Portal,
  Separator,
  Card,
  Spinner,
} from '@chakra-ui/react';
import {
  Menu,
  Search,
  User,
  Users,
  Package,
  BarChart3,
  LogOut,
  Shield,
  Trophy,
  Settings,
  FileText,
  UserCircle,
  Database,
  Wallet,
  MessageCircle,
  TrendingUp,
  Brain,
  Coins,
  ArrowLeftRight,
  ClipboardList,
  Smile,
  Bug,
  LayoutDashboard,
  Gift,
  Layers,
  FileCode,
  Image as ImageLucide,
} from 'lucide-react';
import { useAuth } from '../AuthContext';
import { useUser } from '../UserContext';
import { useEnvironment } from '../contexts/EnvironmentContext';
import HeaderUserSearch from './HeaderUserSearch';
import MoaniPriceHeader from './MoaniPriceHeader';

const drawerWidth = 240;

const navIcons = {
  Dashboard: LayoutDashboard,
  Search: Search,
  Person: User,
  People: Users,
  Collections: Package,
  BarChart: BarChart3,
  Security: Shield,
  EmojiEvents: Trophy,
  SupervisorAccount: Settings,
  RequestPage: FileText,
  AccountCircle: UserCircle,
  DataObject: Database,
  AccountBalanceWallet: Wallet,
  Chat: MessageCircle,
  TrendingUp: TrendingUp,
  Psychology: Brain,
  MonetizationOn: Coins,
  SwapHoriz: ArrowLeftRight,
  Group: Users,
  Assignment: ClipboardList,
  Face: Smile,
  BugReport: Bug,
  Gift: Gift,
  Layers: Layers,
  FileCode: FileCode,
  Image: ImageLucide,
};

function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hoveredGroup, setHoveredGroup] = useState(null);

  const { user, logout } = useAuth();
  const { recentUsers, selectUserById } = useUser();
  const { environment, isDevelopment } = useEnvironment();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navigationGroups = [
    {
      title: 'Dashboard',
      icon: 'Dashboard',
      items: [
        { text: 'Dashboard', icon: 'Dashboard', path: '/' },
        { text: 'Player Activity', icon: 'People', path: '/player-activity' },
        { text: 'User Search', icon: 'Search', path: '/user-search' },
      ]
    },
    {
      title: 'Game',
      icon: 'Collections',
      items: [
        { text: 'Assets', icon: 'Collections', path: '/assets' },
        { text: 'Quests', icon: 'Assignment', path: '/quests' },
        { text: 'Daily Quest Reward Recovery', icon: 'Gift', path: '/quests/daily-reward-recovery' },
        { text: 'NPCs', icon: 'Face', path: '/npcs' },
        { text: 'Trader Catalog', icon: 'Storefront', path: '/trader-catalog' },
        { text: 'Mint Requests', icon: 'RequestPage', path: '/mint-requests' },
        ...(user?.role === 'manager' || user?.role === 'admin' || user?.role === 'super_admin'
          ? [{ text: 'Mint Pipeline', icon: 'Layers', path: '/mint-pipeline' }]
          : []),
        { text: 'V2 contracts', icon: 'FileCode', path: '/mint-pipeline/contracts' },
        ...(user?.role === 'admin' || user?.role === 'super_admin' ? [
          { text: 'NFT metadata', icon: 'Image', path: '/nft-metadata' },
        ] : []),
        ...(user?.role !== 'manager' ? [
          { text: 'Stakes', icon: 'TrendingUp', path: '/stakes' }
        ] : []),
        { text: 'Drop Table Tester', icon: 'Psychology', path: '/drop-table-tester' },
        ...(user?.role === 'super_admin' ? [
          { text: 'Auction Refunds', icon: 'MonetizationOn', path: '/auction-refunds' }
        ] : []),
        { text: 'Events', icon: 'EmojiEvents', path: '/events' },
        { text: 'Event Planner', icon: 'EmojiEvents', path: '/event-planner' },
        { text: 'AI Conversations', icon: 'Chat', path: '/conversations' },
        { text: 'Chat Messages', icon: 'Chat', path: '/chat' },
      ]
    },
    ...(user?.role !== 'manager' ? [{
      title: 'Analytics',
      icon: 'BarChart',
      items: [
        { text: 'Analytics', icon: 'BarChart', path: '/analytics' },
        { text: 'Datapoint Activity', icon: 'DataObject', path: '/datapoint-activity' },
        { text: 'Error Reports', icon: 'BugReport', path: '/error-reports' },
        ...(user?.role === 'super_admin' ? [
          { text: 'Auction Farmers', icon: 'BarChart', path: '/auction-farmers' }
        ] : []),
      ]
    }] : []),
    ...(user?.role === 'admin' || user?.role === 'super_admin' ? [{
      title: 'Allocations',
      icon: 'TrendingUp',
      items: [
        { text: 'Dashboard', icon: 'TrendingUp', path: '/allocations' },
        { text: 'Tracked Wallets', icon: 'AccountBalanceWallet', path: '/allocations/tracked-wallets' },
      ]
    }] : []),
  ];

  if (user?.role === 'manager' || user?.role === 'admin' || user?.role === 'super_admin') {
    const securityItems = [
      { text: 'Auth Logs', icon: 'Security', path: '/auth-logs' }
    ];
    if (user?.role === 'admin' || user?.role === 'super_admin') {
      securityItems.push(
        { text: 'Admin Users', icon: 'SupervisorAccount', path: '/admin-users' },
        { text: 'Wallets', icon: 'AccountBalanceWallet', path: '/wallets' }
      );
    }
    if (user?.role === 'super_admin') {
      securityItems.push(
        { text: 'Security Settings', icon: 'Shield', path: '/security' },
        { text: 'Server Deploy', icon: 'Layers', path: '/server-deploy' },
        { text: 'Blockchain Transfers', icon: 'SwapHoriz', path: '/moani-blockchain-transfers' }
      );
    }
    if (user?.role === 'manager' || user?.role === 'admin' || user?.role === 'super_admin') {
      securityItems.push(
        { text: 'MOANI Transactions', icon: 'MonetizationOn', path: '/moani-transactions' }
      );
    }
    securityItems.push(
      { text: 'Multi-Account Detection', icon: 'Group', path: '/multi-account-detection' }
    );
    if (user.role === 'manager' || user.role === 'owner') {
      securityItems.push(
        { text: 'IP Grouped Users', icon: 'Group', path: '/ip-grouped-users' }
      );
    }
    navigationGroups.push({
      title: 'Security',
      icon: 'Security',
      items: securityItems
    });
  }

  const profileItem = { text: 'Profile', icon: 'AccountCircle', path: '/profile' };

  const allMenuItems = [
    ...navigationGroups.flatMap(group => group.items),
    profileItem
  ];

  const isGroupActive = (group) => {
    return group.items.some(item =>
      item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path)
    );
  };

  const isItemActive = (item) => {
    return item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path);
  };

  const handleGroupHover = (groupIndex) => {
    setHoveredGroup(groupIndex);
  };

  const handleGroupLeave = () => {
    setHoveredGroup(null);
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    setHoveredGroup(null);
  };

  const NavIcon = ({ name }) => {
    const IconComponent = navIcons[name] || User;
    return <IconComponent size={18} />;
  };

  const mobileDrawer = (
    <VStack gap="0" align="stretch" pt="16">
      {allMenuItems.map((item) => (
        <Box
          key={item.text}
          as="button"
          display="flex"
          alignItems="center"
          gap="2"
          px="4"
          py="3"
          w="full"
          textAlign="left"
          bg={isItemActive(item) ? 'rgba(255,255,255,0.1)' : 'transparent'}
          color="white"
          _hover={{ bg: 'rgba(255,255,255,0.08)' }}
          onClick={() => {
            navigate(item.path);
            setMobileOpen(false);
          }}
        >
          <NavIcon name={item.icon} />
          <Text fontSize="0.875rem">{item.text}</Text>
        </Box>
      ))}
    </VStack>
  );

  const desktopDrawer = (
    <VStack gap="0" align="stretch" p="2" h="full" pos="relative">
      <Text fontSize="sm" fontWeight="bold" px="2" mb="2">Recent Users</Text>
      {recentUsers && recentUsers.length > 0 ? (
        <VStack gap="1" align="stretch" flex="1" overflow="auto">
          {recentUsers.map((u) => (
            <Card.Root
              key={u.id}
              size="sm"
              variant="outline"
              cursor="pointer"
              borderWidth={location.pathname.includes(`/user/${u.id}`) ? '2px' : '1px'}
              borderColor={location.pathname.includes(`/user/${u.id}`) ? '#00e5ff' : 'rgba(0,229,255,0.2)'}
              _hover={{
                transform: 'translateY(-1px)',
                boxShadow: 'md',
              }}
              onClick={() => selectUserById(u.id, navigate)}
            >
              <Card.Body p="2">
                <VStack gap="1" align="stretch">
                  <HStack gap="2" align="center">
                    <Avatar.Root
                      size="xs"
                      bg="linear-gradient(135deg, #00e5ff 0%, #ff4081 100%)"
                      color="white"
                      fontWeight="bold"
                    >
                      <Avatar.Fallback>
                        {u.name ? u.name.charAt(0).toUpperCase() : 'U'}
                      </Avatar.Fallback>
                    </Avatar.Root>
                    <Text fontSize="0.875rem" fontWeight="medium" truncate flex="1">
                      {u.name || 'Unnamed User'}
                    </Text>
                  </HStack>
                  <HStack justify="space-between" align="center" gap="1">
                    <Text fontSize="0.7rem" color="fg.muted" truncate flex="1">
                      {u.email}
                    </Text>
                    <HStack gap="1">
                      {(u.banned && u.banned !== 0) && (
                        <Box w="6px" h="6px" borderRadius="full" bg="red.500" title="Banned" />
                      )}
                      {(u.cheater && u.cheater !== 0) && (
                        <Box w="6px" h="6px" borderRadius="full" bg="orange.500" title="Cheater" />
                      )}
                      {u.permaban && (
                        <Box w="6px" h="6px" borderRadius="full" bg="red.700" title="Permaban" />
                      )}
                    </HStack>
                  </HStack>
                </VStack>
              </Card.Body>
            </Card.Root>
          ))}
        </VStack>
      ) : (
        <Card.Root variant="outline" size="sm" p="3" textAlign="center">
          <Text fontSize="0.875rem" color="fg.muted" fontStyle="italic">
            No recent users
          </Text>
        </Card.Root>
      )}
      <Box
        pos="absolute"
        bottom="4"
        left="4"
        right="4"
        textAlign="center"
        borderTop="1px solid rgba(0, 229, 255, 0.2)"
        pt="2"
      >
        <Text fontSize="0.7rem" color="fg.muted" fontFamily="monospace" opacity={0.7}>
          Paradise Tycoon Admin v1.0.0
        </Text>
      </Box>
    </VStack>
  );

  return (
    <Box display="flex" flexDirection="column" height="100vh" minW="100vw" w="full" bg="#0a0a0a" alignItems="center">
      <Box
        as="header"
        position="fixed"
        top="0"
        left="0"
        right="0"
        zIndex="1400"
        bg="#0a0a0a"
        borderBottom="2px solid rgba(0, 229, 255, 0.3)"
        boxShadow="0 4px 20px rgba(0, 229, 255, 0.1)"
        display="flex"
        justifyContent="center"
      >
        <HStack gap="2" p="2" align="center" minH="64px" maxW="1500px" w="full">
          <IconButton
            aria-label="open drawer"
            display={{ base: 'flex', sm: 'none' }}
            variant="ghost"
            color="white"
            onClick={handleDrawerToggle}
            size="sm"
          >
            <Menu size={24} />
          </IconButton>
          <Text fontSize="1.1rem" fontWeight="bold" color="white" whiteSpace="nowrap" mr="2">
            Paradise Admin
          </Text>

          <HStack gap="1" flex="1" display={{ base: 'none', sm: 'flex' }} align="center">
            {navigationGroups.map((group, groupIndex) => (
              <Box
                key={group.title}
                pos="relative"
                mr="1"
                onMouseEnter={() => handleGroupHover(groupIndex)}
                onMouseLeave={handleGroupLeave}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  color={isGroupActive(group) ? 'white' : 'rgba(255,255,255,0.7)'}
                  bg={isGroupActive(group) ? 'rgba(255,255,255,0.1)' : 'transparent'}
                  _hover={{ bg: 'rgba(255,255,255,0.1)' }}
                  fontSize="0.8rem"
                  px="3"
                  py="2"
                  onClick={() => {}}
                >
                  <NavIcon name={group.icon} />
                  <Text as="span" ml="1">{group.title}</Text>
                </Button>

                {hoveredGroup === groupIndex && (
                  <Box
                    pos="absolute"
                    top="100%"
                    left="0"
                    minW="200px"
                    bg="#1a1a1a"
                    boxShadow="lg"
                    borderRadius="4px"
                    zIndex="1500"
                    border="1px solid rgba(255,255,255,0.12)"
                    onMouseEnter={() => handleGroupHover(groupIndex)}
                    onMouseLeave={handleGroupLeave}
                  >
                    <VStack gap="0" align="stretch" py="1">
                      {group.items.map((item) => (
                        <Box
                          key={item.text}
                          as="button"
                          display="flex"
                          alignItems="center"
                          gap="2"
                          py="2"
                          px="4"
                          w="full"
                          textAlign="left"
                          bg={isItemActive(item) ? 'rgba(144,202,249,0.12)' : 'transparent'}
                          color="white"
                          _hover={{
                            bg: isItemActive(item) ? 'rgba(144,202,249,0.2)' : 'rgba(255,255,255,0.08)',
                          }}
                          onClick={() => handleMenuItemClick(item.path)}
                        >
                          <Box minW="36px" display="flex" justifyContent="center">
                            <NavIcon name={item.icon} />
                          </Box>
                          <Text fontSize="0.875rem">{item.text}</Text>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}
              </Box>
            ))}

            <Button
              variant="ghost"
              size="sm"
              color={location.pathname.startsWith(profileItem.path) ? 'white' : 'rgba(255,255,255,0.7)'}
              bg={location.pathname.startsWith(profileItem.path) ? 'rgba(255,255,255,0.1)' : 'transparent'}
              _hover={{ bg: 'rgba(255,255,255,0.1)' }}
              fontSize="0.8rem"
              px="3"
              py="2"
              onClick={() => navigate(profileItem.path)}
            >
              <NavIcon name="AccountCircle" />
              <Text as="span" ml="1">Profile</Text>
            </Button>
          </HStack>

          <Badge
            size="sm"
            bg={isDevelopment ? '#10b981' : '#dc2626'}
            color="white"
            fontWeight="bold"
            fontSize="0.7rem"
            display={{ base: 'none', sm: 'flex' }}
            mr="2"
          >
            {environment === 'production' ? 'PRODUCTION' : 'DEVELOPMENT'}
          </Badge>

          <Box display={{ base: 'none', sm: 'flex' }} mx="2">
            <HeaderUserSearch />
          </Box>

          <Box display={{ base: 'none', sm: 'flex' }} mr="2">
            <MoaniPriceHeader />
          </Box>

          {user && (
            <>
              <Box display={{ base: 'none', sm: 'block' }}>
                <HStack gap="2" align="center">
                  <VStack gap="0" align="flex-end">
                    <Text fontSize="sm" lineHeight="1.2">
                      {user.username} ({user.role?.replace('_', ' ') || 'user'})
                    </Text>
                    <Text fontSize="xs" opacity={0.7} lineHeight="1">
                      {user.email}
                    </Text>
                  </VStack>
                  <Button
                    variant="ghost"
                    size="sm"
                    color="white"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} />
                    <Text as="span" ml="1">Logout</Text>
                  </Button>
                </HStack>
              </Box>

              <Box display={{ base: 'flex', sm: 'none' }} alignItems="center" gap="1">
                <MoaniPriceHeader compact={true} />
                <IconButton
                  variant="ghost"
                  color="white"
                  size="sm"
                  aria-label="Logout"
                  onClick={handleLogout}
                >
                  <LogOut size={20} />
                </IconButton>
              </Box>
            </>
          )}
        </HStack>
      </Box>

      <Box display="flex" flexGrow={1} mt="64px" maxW="1500px" w="full">
        <Box
          as="nav"
          width={{ sm: drawerWidth }}
          flexShrink={0}
        >
          <Drawer.Root open={mobileOpen} onOpenChange={(e) => setMobileOpen(e.open)} placement="start">
            <Portal>
              <Drawer.Backdrop />
              <Drawer.Positioner>
                <Drawer.Content
                  width={drawerWidth}
                  mt="64px"
                  maxH="calc(100vh - 64px)"
                  display={{ base: 'block', sm: 'none' }}
                  bg="#1a1a1a"
                >
                  <Drawer.Body p="0">{mobileDrawer}</Drawer.Body>
                </Drawer.Content>
              </Drawer.Positioner>
            </Portal>
          </Drawer.Root>

          <Box
            display={{ base: 'none', sm: 'block' }}
            width={drawerWidth}
            mt="64px"
            height="calc(100vh - 64px)"
            bg="#0a0a0a"
            borderRight="1px solid rgba(0, 229, 255, 0.2)"
            overflow="auto"
          >
            {desktopDrawer}
          </Box>
        </Box>

        <Box
          as="main"
          flexGrow={1}
          p="6"
          width={{ sm: `calc(100% - ${drawerWidth}px)` }}
          overflow="auto"
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}

export default Layout;
